import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AuthShell, { AlertIcon } from './AuthShell';
import { AuthAction, TranslateAction } from './../../actions';
import { connect } from 'react-redux';
import { resolveMailLinkTarget } from '../../lib/ticketRoute';
import { Translate as t } from '../../config/Translate';
import { PRELOGIN_LANG_OPTIONS, getPreLoginLang, setPreLoginLang } from '../../lib/preLoginLang';
import { loadLang } from '../../config/langResources';
import { resolveUserLangCode } from '../../config/currentLang';
import { resolvePostLoginPath } from '../../lib/loginLanding';
import { consumeSessionExpired } from '../../lib/sessionExpiredNotice';
import { safeGetItem, safeRemoveItem, safeSetItem } from '../../lib/safeStorage';
import LoginEmailNoticeModal, { shouldShowLoginEmailNotice } from './LoginEmailNoticeModal';

// PR #185 R2 — 로그인 직후 hard reload 보장.
// history.push 는 SPA navigation 으로 React tree 가 unmount 되지 않아, 직전 user 로
// 마운트된 컴포넌트들(AdminLeftMenu 등) 의 stale 한 internal state 가 그대로 남을 수 있다.
// 사례: admin 으로 로그인 후 → logout → ExternalDealer 로 재로그인 시 admin 메뉴가 그대로 보임.
// window.location.assign 은 hard reload 라 React tree 전체 재마운트 → stale 위험 0.
const goToAfterLogin = (path) => {
    window.location.assign(path);
};

// Eye icon SVG (visible / hidden)
const EyeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" width="19" height="19">
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="1.6"/>
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
);
const EyeOffIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" width="19" height="19">
        <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M10.6 6.2A9.7 9.7 0 0112 6c6.4 0 10 7 10 7a17 17 0 01-3.3 3.9M6.6 6.7A17 17 0 002 12s3.6 7 10 7a9.6 9.6 0 004-.86" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
);
const LoginPage = ({ user, loggingIn, isAdmin, login, logout, location }) => {
    const [inputs, setInputs] = useState({ username: '', password: '' });
    const { username, password } = inputs;

    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({ username: '', password: '' });
    const [submitting, setSubmitting] = useState(false);
    // U-44: 401 자동 로그아웃으로 도착했으면 만료 안내를 한 번 보여준다(읽으면 표식이 지워진다).
    const [sessionExpired] = useState(consumeSessionExpired);
    const [lang, setLang] = useState(getPreLoginLang);
    // FEAT-063: 이 화면에서 실제로 제출한 로그인 입력값. 저장된 세션으로 진입(loggingIn 초기값 true)한
    // 경우에는 비어 있어 안내 모달이 뜨지 않는다 — 「ID 로 로그인에 성공했을 때만」 띄우기 위한 표식.
    const submittedIdRef = useRef('');
    // 안내 모달을 닫은 뒤 이동할 목적지. 값이 있으면 모달이 열려 있다.
    const [pendingPath, setPendingPath] = useState(null);
    const leavingRef = useRef(false);

    // 목적지 확정 후 이동. ID 로그인이면 안내 모달을 먼저 띄우고, 닫을 때 이동한다.
    const proceedAfterLogin = (path) => {
        if (submittedIdRef.current && shouldShowLoginEmailNotice(submittedIdRef.current, user)) {
            setPendingPath(path);
            return;
        }
        goToAfterLogin(path);
    };

    useEffect(() => {
        if (loggingIn) {
            setSubmitting(false);
            if (user) {
                TranslateAction.SearchByLanguageDirect(resolveUserLangCode(user)).then(translate => {
                    // safeSetItem 이 실패(SecurityError/용량 초과 등)를 흡수하고 warn 하므로
                    // 여기서는 반환값을 무시하고 로그인 진행(BUG-241/242 F-004 — 나머지 raw 접근 전환).
                    safeSetItem('translate', JSON.stringify(translate));
                }).catch(() => {
                    // translate 캐시 갱신 실패는 무시하고 로그인 진행 (unhandled rejection 방지)
                });
            }

            // IMP-483: 로그인 화면 통합(/Login, /Admin/Login) — deep-link 복귀(state.from) 지원.
            const from = location?.state?.from;
            const fromPath = from ? `${from.pathname || ''}${from.search || ''}` : undefined;
            // BUG-241/242 F-004: mailLink 는 아래 'if (mailLink)' 분기 판정과 처리에서만 쓰인다 — 한 번만 읽어 재사용.
            const mailLink = safeGetItem('mailLink');

            // FEAT-052 (#493): 동의 미완료라도 **여기서 갈라지지 않는다.** 가려던 곳으로 그대로
            // 보내고, 그 화면의 라우트 가드(AuthRoute/AdminAuthRoute)가 보호 컴포넌트 대신
            // 동의 모달을 띄운다. 종전에는 이 자리에서 /IubendaLogin 으로 한 번, 동의 후 목적지로
            // 또 한 번 전체 페이지를 이동시켜 이용자가 "또 로그인했다"로 체감했다.
            //
            // 딥링크(state.from)·mailLink 도 이 경로를 그대로 타므로 별도 보존이 필요 없다 —
            // 동의가 끝나면 이미 그 화면에 와 있다.
            if (mailLink) {
                safeRemoveItem('mailLink');
                // IMP-448: 티켓 딥링크면 계정 권한에 맞는 목적지(어드민/일반)로 먼저 확정한다.
                // BUG-101: 승격된 목적지도 resolvePostLoginPath 를 통과 — ExternalDealer/open-redirect 우회 차단.
                proceedAfterLogin(resolvePostLoginPath(user, resolveMailLinkTarget(user, mailLink)));
            } else {
                proceedAfterLogin(resolvePostLoginPath(user, fromPath));
            }
        }
    }, [loggingIn]); // eslint-disable-line react-hooks/exhaustive-deps

    function handleChange(e) {
        const { name, value } = e.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
        // 입력 시 해당 필드 에러 해제
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    }

    // 언어 변경 시 setLang → 컴포넌트 re-render → t()(Translate) 재호출 시
    // getPreLoginLang() 이 새 localStorage 값을 읽어 선택된 언어 텍스트가 반영된다.
    // IMP-328: 언어 청크가 미로드 상태일 수 있으므로 loadLang 완료 후 상태를 반영해
    // 영어 fallback이 잠깐 노출되는 것을 방지한다.
    function handleLangChange(e) {
        const code = e.target.value;
        loadLang(code).then(() => {
            setPreLoginLang(code);
            setLang(code);
        });
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {
            username: username ? '' : t('auth.message.error.idRequired'),
            password: password ? '' : t('auth.message.error.passwordRequired'),
        };
        if (newErrors.username || newErrors.password) {
            setErrors(newErrors);
            return;
        }
        setSubmitting(true);
        submittedIdRef.current = username;
        try {
            await login(username, password);
        } finally {
            setSubmitting(false);
        }
    };

    const isLoading = loggingIn || submitting;

    return (
        <AuthShell
            title="RAYGuard" /* IMP-483: 로그인 화면 통합 — Tech 전용 표기 제거, RAYGuard 원문으로 중립화 */
            description={t('auth.message.info.signInDesc')}
            topRight={(
                /* Language selector */
                <div className="rg-login-lang-wrap">
                    <label htmlFor="rg-login-lang-select" className="rg-login-sr-only">
                        {t('auth.label.selectLanguage')}
                    </label>
                    <select
                        id="rg-login-lang-select"
                        className="rg-login-lang"
                        value={lang}
                        onChange={handleLangChange}
                        aria-label={t('auth.label.selectLanguage')}
                    >
                        {PRELOGIN_LANG_OPTIONS.map(({ code, label }) => (
                            <option key={code} value={code}>{label}</option>
                        ))}
                    </select>
                </div>
            )}
        >
            {sessionExpired && (
                <div className="rg-login-err-msg" role="alert" style={{ marginBottom: 12 }}>
                    <AlertIcon />{t('auth.message.error.sessionExpired')}
                </div>
            )}

            <form noValidate onSubmit={handleSubmit}>
                {/* ID field */}
                <div className={`rg-login-field${errors.username ? ' has-error' : ''}`}>
                    <label htmlFor="rg-login-username">{t('auth.field.loginId')}</label>
                    <div className="rg-login-input-wrap">
                        <input
                            id="rg-login-username"
                            type="text"
                            name="username"
                            value={username}
                            onChange={handleChange}
                            autoComplete="username"
                            aria-describedby="rg-login-id-err"
                            aria-invalid={!!errors.username}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSubmit(e);
                                }
                            }}
                        />
                    </div>
                    {errors.username && (
                        <span className="rg-login-err-msg" id="rg-login-id-err" role="alert">
                            <AlertIcon />{errors.username}
                        </span>
                    )}
                </div>

                {/* Password field */}
                <div className={`rg-login-field${errors.password ? ' has-error' : ''}`}>
                    <label htmlFor="rg-login-password">{t('auth.field.password')}</label>
                    <div className="rg-login-input-wrap">
                        <input
                            id="rg-login-password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={password}
                            onChange={handleChange}
                            autoComplete="current-password"
                            aria-describedby="rg-login-pw-err"
                            aria-invalid={!!errors.password}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSubmit(e);
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="rg-login-icon-btn"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-pressed={showPassword}
                            aria-label={showPassword ? t('auth.label.hidePassword') : t('auth.label.showPassword')}
                        >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    </div>
                    {errors.password && (
                        <span className="rg-login-err-msg" id="rg-login-pw-err" role="alert">
                            <AlertIcon />{errors.password}
                        </span>
                    )}
                </div>

                {/* U-30: '로그인 상태 유지'는 토큰 보존 로직이 없어(체크해도 동작 동일) 구현 전까지 숨김 */}
                <div className="rg-login-row-between" style={{ justifyContent: 'flex-end' }}>
                    <Link to="/ForgotPassword" className="rg-login-link">
                        {t('auth.label.forgotYourPassword')}
                    </Link>
                </div>

                {/* Submit button */}
                <button
                    type="submit"
                    className="rg-login-btn-signin"
                    disabled={isLoading}
                    aria-busy={isLoading}
                >
                    {isLoading && <span className="rg-login-spinner" aria-hidden="true" />}
                    <span>{t('auth.button.signIn')}</span>
                </button>
            </form>
            <LoginEmailNoticeModal
                show={!!pendingPath}
                email={user?.email}
                onClose={() => {
                    // 확인 연타·ESC 중복으로 이동이 여러 번 시작되지 않게 한 번만 이동한다.
                    if (leavingRef.current) return;
                    leavingRef.current = true;
                    goToAfterLogin(pendingPath);
                }} />
        </AuthShell>
    );
};

const mapState = (state) => {
    return {
        loggingIn: state.AuthReducer.loggingIn,
        user: state.AuthReducer.user,
    };
};

const mapDispatch = (dispatch) => ({
    login: (username, password) => dispatch(AuthAction.Login(username, password)),
    logout: () => dispatch(AuthAction.Logout()),
});

export default connect(mapState, mapDispatch)(LoginPage);
